import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";

import type { Transport } from "@tdelivr/api-client";
import { services } from "@tdelivr/api-client";
// The row shape NTF-07 publishes, imported rather than restated.
import type { TemplateSummary } from "@tdelivr/api-client/generated/notifications/template.js";

import { boundedNote, PER_FETCH, type BoardRow } from "../board.ts";
import { filterTemplates, templateGroups, templateRow, TEMPLATE_STATES } from "../templates.ts";
import { stateForError, type ViewState } from "../view-state.ts";
import { RecordsBoard } from "./RecordsBoard.tsx";

/**
 * Messages and templates: the SMS, push, email and in-app messages this tenant's customers receive.
 *
 * Built to `17 WP16 Tenant Admin.dc.html`, page `templates`, against NTF-07
 * `GET /v1/notification-templates`.
 *
 * **The design names two services for this page and neither holds a template.** Its chip reads
 * `NTF-02 · NTF-06` — the SMS adapter and the notification-preference service — and this chip reads
 * `NTF-07`, which is the service that owns templates and the one this board reads. Q-149, and it is
 * R-17's shape from the other direction: there a page was recorded as unsupported by services the
 * design named while a service it did not name answered it; here the citation is wrong and the page
 * was blocked anyway, because NTF-07 was internal too.
 *
 * **What the listing withholds is the message.** `content` is up to ten thousand characters of body
 * with `{{variable}}` placeholders in it, `subject` is the email line, and `allowedVariables` is the
 * list of fields a body may carry about a customer. The board draws none of the three, and the
 * render operation that interpolates a customer's data into a body is not published under `/v1` at
 * all. ADR 0043.
 *
 * **One designed row is many published rows, and the board groups them.** NTF-07's record is a
 * variant — one `(templateKey, locale, channel)` — and the design's row is a template whose
 * `Channel` and `Languages` columns are lists. `templates.ts` groups in the order the listing
 * answered, which keeps `Default order` the service's own.
 *
 * **No write requirement.** The design's form is `New message template → Save draft` over a
 * template name, a channel set, an event and two message bodies. NTF-07's draft command takes one
 * locale and one channel per call, so the form's `Channel` chip set is several commands rather than
 * one; it has no event field at all — nothing on NTF-07 associates a template with the event that
 * sends it, which is NTF-01's orchestration — and the command is not published under `/v1`. Q-152.
 */

const templatesApi = services.notifications__template;

export interface MessageTemplatesProps {
  readonly transport: Transport;
  /** Innermost last: tenant, group, page. Rendered in the page body, never in the top bar. */
  readonly crumbs?: readonly string[];
}

interface Loaded {
  readonly templates: readonly TemplateSummary[];
  readonly truncated: boolean;
}

export function MessageTemplates({ transport, crumbs }: MessageTemplatesProps): ReactNode {
  const [loaded, setLoaded] = useState<Loaded | undefined>(undefined);
  const [failure, setFailure] = useState<ViewState | undefined>(undefined);
  const [state, setState] = useState("");

  const load = useCallback(async () => {
    const client = templatesApi.createTemplateClient(transport);
    try {
      const answered = await client.listTenantTemplateSummaries(PER_FETCH, undefined);
      setLoaded({
        templates: answered.templates,
        truncated: typeof answered.nextAfter === "string" && answered.nextAfter.length > 0,
      });
      setFailure(undefined);
    } catch (error) {
      setFailure(stateForError(error, ["template:summary:read"]));
      setLoaded(undefined);
    }
  }, [transport]);

  useEffect(() => {
    void load();
  }, [load]);

  const rows: readonly BoardRow[] | undefined = useMemo(() => {
    if (loaded === undefined) return undefined;
    return filterTemplates(templateGroups(loaded.templates), state).map(templateRow);
  }, [loaded, state]);

  /*
   * The floor is in variants, not in templates, and the sentence says so.
   *
   * `boundedNote` reports what the listing answered, and the listing answers variants — so a
   * truncated page of two hundred variants is some number of templates this board cannot name. The
   * noun is the honest one rather than the one the grid's rows are counted in.
   */
  const bounded = boundedNote(
    loaded?.templates.length,
    loaded?.truncated ?? false,
    "template variants",
    "ordered by template, language and channel",
  );

  return (
    <RecordsBoard
      crumbs={crumbs ?? []}
      title="Messages and templates"
      source="NTF-07"
      description="SMS, push, email and WhatsApp messages your customers and drivers receive."
      columns={["Template", "Channel", "Languages"]}
      rows={rows}
      {...(failure === undefined ? {} : { failure })}
      onRetry={() => {
        void load();
      }}
      purpose="Templates appear here as soon as somebody creates a draft of one for this tenant."
      primary={{
        label: "New template",
        refusal:
          "NTF-07's draft command takes one language and one channel per call, where the design's form offers a set of each, and it has no field for the event that sends a message — nothing on NTF-07 associates a template with an event. The command is also not published under /v1: ADR 0043 publishes the read and nothing else. Q-152.",
      }}
      filter={{
        unfiltered: "All states",
        options: TEMPLATE_STATES.map((value) => ({ value, label: value })),
        value: state,
        onChange: setState,
      }}
      note={{
        /*
         * The design's `info` note is replaced and keeps its kind, its glyph and its place.
         *
         * It reads "Previews render with sample data; variables that could be empty are flagged."
         * Both halves describe the render operation, which ADR 0043 deliberately does not publish —
         * a browser rendering a template with variables it chose is a console composing a message in
         * a customer's name. And nothing flags a variable that could be empty: NTF-07 refuses a
         * render **missing** any allowed variable outright, which is a stronger rule than the design
         * describes and is the one worth telling an administrator.
         */
        kind: "info",
        glyph: "info",
        text: "A message is sent from the published version of one language-and-channel variant. A render missing any of its allowed variables is refused rather than sent with a blank.",
      }}
      {...(bounded === undefined ? {} : { bounded })}
      footnote="One row per template, at the newest version of each of its language-and-channel variants. Which languages a tenant requires is not published anywhere, so this board says which a template has and never which it lacks."
      exportName="message-templates"
      detailSubtitle="Messages and templates · NTF-07"
      detailNote="A message is sent from the published version of one language-and-channel variant. A render missing any of its allowed variables is refused rather than sent with a blank."
    />
  );
}
